import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import type { LandingPageContent } from "@shared/landingPages";
import { buildLandingPageFaqJsonLd } from "@shared/landingPages";
import { trackEvent, trackLandingEvent } from "../../lib/analytics";

type PageKind = "dscsa" | "software";

/**
 * Shared managed-content renderer. The fields remain editable in the admin
 * workspace; the page kind only changes the framing around those fields.
 */
export default function ManagedContentSections({
  content,
  pageKind = "dscsa",
}: {
  content: LandingPageContent;
  pageKind?: PageKind;
}) {
  const pageUrl = pageKind === "software" ? "/software" : "/lp/dscsa-compliance";
  const isSoftware = pageKind === "software";
  const trackCta = (location: string) => {
    trackLandingEvent("landing_page_cta_click", pageUrl, {
      buttonLabel: content.ctaText,
      buttonLocation: location,
    });
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(buildLandingPageFaqJsonLd(content)).replace(/<\/script>/gi, "<\\/script>")}
        </script>
      </Helmet>

      <section className="bg-white py-16 lg:py-24" aria-labelledby={`${pageKind}-offer-heading`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-14 items-stretch">
            <div className="flex flex-col justify-center">
              <p className="text-sm font-semibold uppercase tracking-widest text-accent">
                {isSoftware ? "Start with the right level" : "A transparent starting point"}
              </p>
              <h2 id={`${pageKind}-offer-heading`} className="text-3xl sm:text-4xl font-bold text-primary mt-3 tracking-tight">
                {content.offer.name}
              </h2>
              <p className="text-gray-600 mt-4 leading-relaxed">{content.offer.eligibility}</p>
              <div className="flex flex-wrap gap-2 mt-6" aria-label="Offer details">
                {content.offer.reassurance.map((item) => (
                  <span key={item} className="rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800 px-3 py-1.5 text-sm font-semibold">
                    {item}
                  </span>
                ))}
              </div>
              <Link to={content.ctaDestination} onClick={() => trackCta("offer-cta")} className="btn-primary inline-flex mt-7 w-fit px-6 py-3">
                {content.ctaText}
              </Link>
            </div>

            <div className="rounded-3xl bg-primary text-white p-7 sm:p-9 shadow-xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-300">
                Included when you start
              </p>
              <ul className="mt-6 space-y-5">
                {content.offer.included.map((item) => (
                  <li key={item} className="flex gap-3 items-start">
                    <span className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                      <svg className="w-4 h-4 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-sm text-white/90 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-white/65 mt-7 leading-relaxed border-t border-white/10 pt-5">
                {content.offer.optionalNextSteps}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background py-16 lg:py-24" aria-labelledby={`${pageKind}-benefits-heading`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-accent">
              {isSoftware ? "Capabilities to compare" : "Operational benefits"}
            </p>
            <h2 id={`${pageKind}-benefits-heading`} className="text-3xl sm:text-4xl font-bold text-primary mt-3 tracking-tight">
              {isSoftware ? "Choose the workflow that fits your practice" : "Turn a complicated requirement into visible next steps"}
            </h2>
          </div>
          <ol className="mt-10 grid md:grid-cols-3 gap-5">
            {content.proof.map((item, index) => (
              <li key={item} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <span className="text-4xl font-extrabold text-accent/25 leading-none tabular-nums" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <p className="text-base text-primary font-semibold leading-relaxed mt-5">{item}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" aria-labelledby={`${pageKind}-faq-heading`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-accent text-center">Common questions</p>
          <h2 id={`${pageKind}-faq-heading`} className="text-3xl sm:text-4xl font-bold text-primary text-center mt-3 tracking-tight">
            {isSoftware ? "Before you choose a plan" : "Before you start your workflow"}
          </h2>
          <div className="mt-10 divide-y divide-gray-200 border-y border-gray-200">
            {content.faqs.map((faq, index) => (
              <details
                key={faq.question}
                className="group py-1"
                onToggle={(event) => {
                  if (event.currentTarget.open) {
                    trackEvent("faq_interaction", { page: pageUrl, question: faq.question, index });
                    trackLandingEvent("landing_page_faq_open", pageUrl, { question: faq.question, index });
                  }
                }}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-5 px-1 py-5 font-semibold text-primary rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2">
                  <span>{faq.question}</span>
                  <span className="text-accent text-2xl leading-none group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
                </summary>
                <p className="text-sm text-gray-600 leading-relaxed pb-5 pr-10">{faq.answer}</p>
              </details>
            ))}
          </div>
          {!isSoftware && (
            <p className="text-xs text-gray-500 leading-relaxed mt-8">
              Educational information only. ScriptTrack and this assessment do not provide legal advice, a regulatory determination, or a guarantee of compliance. Confirm current requirements with FDA guidance and your compliance advisor.
            </p>
          )}
          <div className="text-center mt-10">
            <Link to={content.ctaDestination} onClick={() => trackCta("final-cta")} className="btn-primary inline-flex px-7 py-3.5">
              {content.ctaText}
            </Link>
            <p className="text-xs text-gray-500 mt-3">{content.reassurance}</p>
          </div>
        </div>
      </section>
    </>
  );
}